import { useEffect, useState, useMemo } from "react";
import { Search, ChevronLeft, ChevronRight } from "lucide-react";
import { http, rupiah } from "@/lib/api";
import AdminShell, { PageHead } from "@/components/AdminShell";

const PAY = {
  unpaid: "Belum Bayar",
  pending: "Menunggu",
  paid: "Lunas",
  expired: "Kadaluarsa",
  cancelled: "Dibatalkan",
  failed: "Gagal",
};

const STATUS_STYLE = {
  paid: "bg-emerald-500/15 text-emerald-500",
  pending: "bg-amber-500/15 text-amber-500",
  expired: "bg-orange-500/15 text-orange-500",
  cancelled: "bg-slate-500/15 text-slate-400",
  failed: "bg-red-500/15 text-red-500",
  unpaid: "bg-muted text-muted-foreground",
};

export default function Payments() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [size, setSize] = useState(
    () => Number(localStorage.getItem("wm_pay_size")) || 10
  );
  const [page, setPage] = useState(1);

  useEffect(() => {
    let cancelled = false;

    async function loadPayments() {
      try {
        const { data } = await http.get("/admin/payments");
        if (!cancelled) {
          setRows(data);
        }
      } catch {}
    }

    // Langsung sinkron saat halaman dibuka.
    loadPayments();

    // Tetap sinkron otomatis selama menu Pembayaran sedang dibuka.
    const timer = setInterval(loadPayments, 30000);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();

    if (!s) return rows;

    return rows.filter(
      (r) =>
        r.order_id.toLowerCase().includes(s) ||
        (r.customer_name || "").toLowerCase().includes(s) ||
        (r.method || "").toLowerCase().includes(s) ||
        (PAY[r.status] || r.status || "").toLowerCase().includes(s)
    );
  }, [rows, q]);

  const totalPages = Math.max(
    1,
    Math.ceil(filtered.length / size)
  );

  const pageItems = filtered.slice(
    (page - 1) * size,
    page * size
  );

  useEffect(() => {
    setPage(1);
  }, [q, size]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  function changeSize(v) {
    setSize(v);
    localStorage.setItem(
      "wm_pay_size",
      String(v)
    );
  }

  return (
    <AdminShell>
      <PageHead
        eyebrow="Keuangan"
        title="Pembayaran"
        desc="Riwayat semua transaksi."
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="flex flex-1 items-center gap-2 rounded-xl border border-border bg-card px-3">
          <Search
            size={16}
            className="text-muted-foreground"
          />

          <input
            data-testid="payment-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cari ID / nama / metode / status"
            className="w-full bg-transparent py-2.5 text-sm outline-none"
          />
        </div>

        <select
          data-testid="payment-page-size"
          value={size}
          onChange={(e) =>
            changeSize(Number(e.target.value))
          }
          className="rounded-xl border border-border bg-card px-3 py-2.5 text-sm font-semibold outline-none"
        >
          {[10, 20, 50, 70].map((n) => (
            <option
              key={n}
              value={n}
            >
              {`${n} / halaman`}
            </option>
          ))}
        </select>
      </div>

      <div className="space-y-2">
        {pageItems.map((r) => (
          <div
            key={r.order_id}
            className="flex items-center justify-between rounded-2xl border border-border bg-card p-4"
          >
            <div>
              <div className="font-heading text-sm font-bold text-card-foreground">
                {r.order_id}
              </div>

              <div className="text-xs text-muted-foreground">
                {r.customer_name} ·{" "}
                {new Date(r.created_at).toLocaleString(
                  "id-ID",
                  {
                    dateStyle: "medium",
                    timeStyle: "short",
                  }
                )}
              </div>
            </div>

            <div className="flex items-center gap-3">
              <span className="rounded-full bg-primary/15 px-2.5 py-1 text-[10px] font-bold uppercase text-primary">
                {r.method}
              </span>

              <span
                className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase ${
                  STATUS_STYLE[r.status] ||
                  "bg-muted text-muted-foreground"
                }`}
              >
                {PAY[r.status] || r.status || "-"}
              </span>

              <b className="font-heading text-sm">
                {rupiah(r.total)}
              </b>
            </div>
          </div>
        ))}

        {filtered.length === 0 && (
          <div className="rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
            Tidak ada transaksi.
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="mt-5 flex items-center justify-center gap-3">
          <button
            disabled={page <= 1}
            onClick={() =>
              setPage((p) => Math.max(1, p - 1))
            }
            className="grid h-9 w-9 place-items-center rounded-xl bg-card disabled:opacity-40"
          >
            <ChevronLeft size={16} />
          </button>

          <span
            data-testid="payment-page-indicator"
            className="text-sm font-semibold text-muted-foreground"
          >
            {page} / {totalPages}
          </span>

          <button
            disabled={page >= totalPages}
            onClick={() =>
              setPage((p) =>
                Math.min(totalPages, p + 1)
              )
            }
            className="grid h-9 w-9 place-items-center rounded-xl bg-card disabled:opacity-40"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}
    </AdminShell>
  );
}
